import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './StickyHeaderAction.scss';

// Height of the app header bar — the line the original action has to pass
// before the docked copy takes over.
const HEADER_HEIGHT = 64;

/**
 * Keeps a page's primary action reachable while the user scrolls.
 *
 * The action renders in place as usual. A second, compact copy is portaled
 * into the header's action slot and fades in once the original has scrolled
 * up behind the header; scrolling back to the top fades it out again.
 *
 * Both copies stay mounted the whole time, so switching between them is a
 * pure CSS transition — nothing moves in the DOM, the page never reflows,
 * and there is no flicker at the hand-over point. While hidden, the docked
 * copy collapses to zero width (grid 0fr → 1fr) so it reserves no space in
 * the header, and `visibility: hidden` keeps it out of the tab order.
 *
 * `accent` re-supplies the page's accent custom property: the portal escapes
 * the page's DOM subtree, so `--ha` would otherwise not be inherited.
 */
function StickyHeaderAction({ children, accent }) {
  const originRef = useRef(null);
  const [docked, setDocked] = useState(false);
  const [slot, setSlot] = useState(null);

  // The header is mounted by the layout in the same commit as the page, so
  // the slot exists by the time effects run.
  useEffect(() => {
    setSlot(document.getElementById('header-action-slot'));
  }, []);

  useEffect(() => {
    const el = originRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Dock only once the original has passed *above* the header — an
        // action that is merely below the fold is not intersecting either.
        setDocked(!entry.isIntersecting && entry.boundingClientRect.top < HEADER_HEIGHT);
      },
      { rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <span ref={originRef} className="sticky-action__origin">{children}</span>
      {slot && createPortal(
        <div
          className={`sticky-action__docked${docked ? ' is-docked' : ''}`}
          style={accent ? { '--ha': accent } : undefined}
          aria-hidden={!docked}
        >
          <div className="sticky-action__docked-inner">{children}</div>
        </div>,
        slot
      )}
    </>
  );
}

export default StickyHeaderAction;
